<sbnb-loading-spinner
  *ngIf="(loading$ | async) && (pricingFeeDiscountData$ | async) === null"
  class="py-9"></sbnb-loading-spinner>
<ng-container *ngIf="pricingFeeDiscountData$ | async as feeDiscountData">
  <div data-element="fees-taxes-discounts-container" class="@container fees-taxes-discounts-container">
    <!-- Security deposit -->
    <mat-divider *ngIf="!section" class="!my-6"></mat-divider>
    <div *ngIf="!section || section === 'security-deposit'">
      <div class="flex items-center justify-between gap-3 mb-4">
        <h3 class="flex-1" id="security-deposit">
          <div class="flex items-center gap-4">
            <span>Security deposit</span>
          </div>
        </h3>
        <sbnb-save-indicator [saveStatus]="(priceSaveState$ | async)?.securityDeposits"></sbnb-save-indicator>
      </div>

      <div>
        <div class="flex @md:items-center @md:gap-3 gap-1 mb-4 @md:flex-row flex-col">
          <span class="flex-1 text-sm flex flex-col gap-1">
            <span class="flex items-center justify-between">
              <span class="strong flex-1">Airbnb deposit amount</span>
              <sbnb-icon-airbnb [size]="12" class="@md:hidden mr-[2px]"></sbnb-icon-airbnb>
            </span>
            <span class="text-sm text-night">
              This will be shown to the guest at checkout and must be collected manually as an offline payment.
              <a
                class="link"
                href="https://help.hospitable.com/en/articles/6262321-security-deposits-for-airbnb"
                target="_blank">
                Learn more
              </a>
            </span>
          </span>

          <sbnb-icon-airbnb [size]="12" class="hidden @md:block"></sbnb-icon-airbnb>
          <mat-form-field class="nopadding input__mini w-full @md:!max-w-[120px]">
            <span matPrefix class="prefix-bg">
              <span class="text__small" style="padding-right: 12px">{{ property.currency | currencyCode }}</span>
            </span>
            <input
              matInput
              inputmode="numeric"
              maxlength="6"
              autocomplete="off"
              placeholder="0"
              [(ngModel)]="feeDiscountData['security_deposit'].value"
              (change)="savePriceChange('security_deposit', feeDiscountData['security_deposit'])" />
          </mat-form-field>
        </div>

        <div class="flex @md:items-center @md:gap-3 gap-1 mb-2 @md:flex-row flex-col">
          <span class="flex-1 text-sm flex flex-col gap-1">
            <span class="flex items-center justify-between">
              <span class="strong flex-1">
                {{
                  (featureFlagDirectSecurityDepositsEnabled$ | async)
                    ? 'Direct security deposit amount'
                    : 'Direct deposit amount'
                }}
              </span>
              <sbnb-property-update-status
                class="@md:hidden mr-[2px]"
                [edited]="feeDiscountData['direct_security_deposit'].edited"
                [hasAirbnbListing]="false"></sbnb-property-update-status>
            </span>
            <span class="text-sm text-night">
              {{
                (featureFlagDirectSecurityDepositsEnabled$ | async)
                  ? "We'll collect the security deposit automatically 2 days before check-in and release it 2 days after check-out. Guests are notified automatically."
                  : 'This can be communicated to Direct guests using a shortcode. It must be collected manually as an offline payment.'
              }}
            </span>
          </span>

          <sbnb-property-update-status
            class="hidden @md:block"
            [edited]="feeDiscountData['direct_security_deposit'].edited"
            [hasAirbnbListing]="false"></sbnb-property-update-status>

          <mat-form-field class="input__mini nopadding w-full @md:!max-w-[120px]">
            <span matPrefix class="prefix-bg">
              <span class="text__small" style="padding-right: 12px">{{ property.currency | currencyCode }}</span>
            </span>
            <input
              matInput
              inputmode="numeric"
              maxlength="6"
              autocomplete="off"
              placeholder="0"
              [(ngModel)]="feeDiscountData['direct_security_deposit'].value"
              (change)="savePriceChange('direct_security_deposit', feeDiscountData['direct_security_deposit'])" />
          </mat-form-field>
        </div>
      </div>
    </div>

    <!-- Occupancy based -->
    <mat-divider *ngIf="!section" class="!my-6"></mat-divider>
    <div *ngIf="!section || section === 'occupancy-based'">
      <div class="flex items-center justify-between gap-3 mb-4">
        <div class="flex flex-col gap-1">
          <h3 class="flex-1" id="fees">Occupancy based</h3>
          <ng-template [ngTemplateOutlet]="iconLegend"></ng-template>
        </div>
        <sbnb-save-indicator [saveStatus]="(priceSaveState$ | async)?.occupancyBased"></sbnb-save-indicator>
      </div>

      <div
        *ngFor="let occupancy of occupancyBased"
        class="flex @md:items-center @md:gap-3 gap-1 mb-4 @md:mb-2 @md:flex-row flex-col">
        <span class="flex-1 text-sm flex flex-col gap-1">
          <span class="flex items-center justify-between">
            <span class="strong flex-1">{{ occupancy.label }}</span>
            <sbnb-property-update-status
              [edited]="feeDiscountData[occupancy.key].edited"
              [hasAirbnbListing]="property.counts?.airbnb > 0"
              [hasBookingListing]="
                occupancy.key === 'maximum_occupancy'
                  ? property.counts?.booking_managed > 0
                  : property.counts.booking > 0
              "
              class="@md:hidden mr-[2px]"></sbnb-property-update-status>
          </span>
          <span *ngIf="occupancy.description" class="text-sm text-night">
            {{ occupancy.description }}
          </span>
        </span>
        <div class="flex flex-col gap-2 @md:items-end">
          <div class="flex gap-3 items-center">
            <sbnb-property-update-status
              [edited]="feeDiscountData[occupancy.key].edited"
              [hasAirbnbListing]="property.counts?.airbnb > 0"
              [hasBookingListing]="
                occupancy.key === 'maximum_occupancy'
                  ? property.counts?.booking_managed > 0
                  : property.counts.booking > 0
              "
              class="hidden @md:block"></sbnb-property-update-status>

            <mat-form-field class="input__mini w-full nopadding @md:!max-w-[120px]">
              <span *ngIf="occupancy.key === 'price_per_extra_person'" matPrefix class="prefix-bg">
                <span class="text__small" style="padding-right: 12px">{{ property.currency | currencyCode }}</span>
              </span>
              <input
                matInput
                type="tel"
                maxlength="6"
                autocomplete="off"
                placeholder="0"
                [(ngModel)]="feeDiscountData[occupancy.key].value"
                (change)="savePriceChange(occupancy.key, feeDiscountData[occupancy.key])"
                [disabled]="occupancy.key === 'maximum_occupancy'"
                [matTooltip]="occupancy.key === 'maximum_occupancy' ? 'Update in property details' : ''" />
              <span matSuffix *ngIf="occupancy.key !== 'price_per_extra_person'">guests</span>
            </mat-form-field>
          </div>
          <a
            *ngIf="occupancy.key === 'maximum_occupancy'"
            class="link-grape text-xs"
            [routerLink]="['/properties/property/', property.id, 'details']">
            Edit in property details
          </a>
        </div>
      </div>
    </div>

    <!-- Fees -->
    <mat-divider *ngIf="!section" class="!my-6"></mat-divider>
    <ng-container *ngIf="!section || section === 'fees'">
      <!-- Old airbnb fees -->
      <div *ngIf="(featureFlagAirbnbFeesEnabled$ | async) !== true; else airbnbFees">
        <div class="flex items-center justify-between gap-3 mb-4">
          <div class="flex flex-col gap-1">
            <h3 class="flex-1" id="fees">Fees</h3>
            <ng-template [ngTemplateOutlet]="iconLegend"></ng-template>
          </div>
          <sbnb-save-indicator [saveStatus]="(priceSaveState$ | async)?.fees"></sbnb-save-indicator>
        </div>

        <div *ngFor="let fee of fees" class="flex @md:items-center @md:gap-3 gap-1 mb-4 @md:mb-2 @md:flex-row flex-col">
          <span class="flex-1 text-sm flex flex-col gap-1">
            <span class="flex items-center justify-between">
              <span class="strong flex-1">{{ fee.label }}</span>
              <sbnb-property-update-status
                [edited]="feeDiscountData[fee.key].edited"
                [hasAirbnbListing]="property.counts?.airbnb > 0"
                [hasBookingListing]="property.counts?.booking_managed > 0"
                class="@md:hidden mr-[2px]"></sbnb-property-update-status>
            </span>
            <span *ngIf="fee.description" class="text-sm text-night">
              {{ fee.description }}
            </span>
          </span>

          <sbnb-property-update-status
            [edited]="feeDiscountData[fee.key].edited"
            [hasAirbnbListing]="property.counts?.airbnb > 0"
            [hasBookingListing]="property.counts?.booking_managed > 0"
            class="hidden @md:block"></sbnb-property-update-status>

          <mat-form-field class="input__mini w-full nopadding @md:!max-w-[120px]">
            <input
              matInput
              inputmode="numeric"
              maxlength="6"
              autocomplete="off"
              placeholder="0"
              [(ngModel)]="feeDiscountData[fee.key].value"
              (change)="savePriceChange(fee.key, feeDiscountData[fee.key])" />
            <span
              *ngIf="fee.fixed || fee.percentage"
              matSuffix
              class="text-xs suffix-bg"
              [class.pointer-events]="fee.fixed === false || fee.percentage === false"
              [matMenuTriggerFor]="typeMenu">
              {{ feeDiscountData[fee.key].type === 'fixed' ? property.currency : '%' }}
              <img
                *ngIf="fee.fixed && fee.percentage"
                class="clickable"
                style="width: 12px; margin-left: 4px"
                src="/assets/images/down-arrow.svg" />
            </span>

            <mat-menu #typeMenu="matMenu">
              <button
                mat-menu-item
                (click)="feeDiscountData[fee.key].type = 'fixed'; savePriceChange(fee.key, feeDiscountData[fee.key])">
                {{ property.currency }}
              </button>
              <button
                mat-menu-item
                (click)="feeDiscountData[fee.key].type = 'percent'; savePriceChange(fee.key, feeDiscountData[fee.key])">
                %
              </button>
            </mat-menu>
          </mat-form-field>
        </div>
      </div>
    </ng-container>

    <!-- Airbnb fees -->
    <ng-template #airbnbFees>
      <div>
        <div class="flex items-center justify-between gap-3 pr-4 mb-4">
          <div class="flex flex-col gap-1">
            <h3 class="flex-1" id="fees">Fees</h3>
            <ng-template [ngTemplateOutlet]="iconLegend"></ng-template>
          </div>
          <sbnb-save-indicator [saveStatus]="(priceSaveState$ | async)?.fees"></sbnb-save-indicator>
        </div>

        <div class="mb-4 text-sm text-night">
          These fees are managed by Hospitable.com. Learn more about fee management in
          <a
            href="https://help.hospitable.com/en/articles/6141381-property-details-pricing"
            target="_blank"
            class="link-grape">
            our help center
          </a>
        </div>

        <div *ngFor="let fee of fees" class="flex @md:items-center @md:gap-3 gap-1 mb-4 @md:mb-2 @md:flex-row flex-col @md:h-11">
          <span class="flex-1 text-sm flex flex-col gap-1">
            <span class="flex items-center justify-between">
              <span class="strong flex-1">{{ fee.label }}</span>
              <sbnb-property-update-status
                [edited]="feeDiscountData[fee.key].edited"
                [hasAirbnbListing]="property.counts?.airbnb > 0"
                [hasBookingListing]="property.counts?.booking_managed > 0"
                class="@md:hidden mr-[2px]"></sbnb-property-update-status>
            </span>
            <span *ngIf="fee.description" class="text-sm text-night">
              {{ fee.descriptionFeatureFlag ?? fee.description }}
            </span>
          </span>

          <sbnb-property-update-status
            [edited]="feeDiscountData[fee.key].edited"
            [hasAirbnbListing]="property.counts?.airbnb > 0"
            [hasBookingListing]="property.counts?.booking_managed > 0"
            class="hidden @md:block"></sbnb-property-update-status>

          <div class="flex gap-3">
            <mat-form-field class="input__mini nopadding flex-auto max-w-[160px]" *ngIf="fee.calculationTypes">
              <mat-select [disabled]="fee.calculationTypes.length === 1" [(ngModel)]="feeDiscountData[fee.key].calculation_type"
                (selectionChange)="savePriceChange(fee.key, feeDiscountData[fee.key])">
                <mat-option *ngFor="let calculationType of fee.calculationTypes" [value]="calculationType.value"
                  [disabled]="!calculationType[feeDiscountData[fee.key].type]">
                  {{ calculationType.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field class="input__mini nopadding @md:max-w-[120px]">
              <input
                matInput
                inputmode="numeric"
                maxlength="6"
                autocomplete="off"
                placeholder="0"
                [(ngModel)]="feeDiscountData[fee.key].value"
                (change)="savePriceChange(fee.key, feeDiscountData[fee.key])" />
              <span
                *ngIf="fee.fixed || fee.percentage"
                matSuffix
                class="text-xs suffix-bg"
                [class.pointer-events]="fee.fixed === false || fee.percentage === false"
                [matMenuTriggerFor]="typeMenu">
                {{ feeDiscountData[fee.key].type === 'fixed' ? property.currency : '%' }}
                <img
                  *ngIf="fee.fixed && fee.percentage"
                  class="clickable"
                  style="width: 12px; margin-left: 4px"
                  src="/assets/images/down-arrow.svg" />
              </span>
            </mat-form-field>

            <mat-menu #typeMenu="matMenu">
              <button
                mat-menu-item
                (click)="
                  feeDiscountData[fee.key].type = 'fixed';
                  invlidateIncompatibleCalculationType(fee.key, feeDiscountData[fee.key], 'fixed');
                  savePriceChange(fee.key, feeDiscountData[fee.key])
                ">
                {{ property.currency }}
              </button>
              <button
                mat-menu-item
                (click)="
                  feeDiscountData[fee.key].type = 'percent';
                  invlidateIncompatibleCalculationType(fee.key, feeDiscountData[fee.key], 'percent');
                  savePriceChange(fee.key, feeDiscountData[fee.key])
                ">
                %
              </button>
            </mat-menu>
          </div>
        </div>
      </div>
    </ng-template>

    <!-- Discounts -->
    <mat-divider *ngIf="!section" class="!my-6"></mat-divider>
    <div *ngIf="!section || section === 'discounts'">
      <div class="flex items-center justify-between gap-3 mb-4">
        <div class="flex flex-col gap-1">
          <h3 class="flex-1" id="discounts">Discounts</h3>
          <ng-template [ngTemplateOutlet]="iconLegend"></ng-template>
        </div>
        <sbnb-save-indicator [saveStatus]="(priceSaveState$ | async)?.discounts"></sbnb-save-indicator>
      </div>
      <ng-container *ngFor="let discount of discounts">
        <ng-container [ngSwitch]="discount.key">
          <ng-container *ngSwitchDefault>
            <div class="flex @md:items-center @md:gap-3 gap-1 mb-4 @md:mb-2 @md:flex-row flex-col">
              <span class="flex-1 text-sm flex flex-col gap-1">
                <span class="flex items-center justify-between">
                  <span class="strong flex-1">{{ discount.label }}</span>
                  <sbnb-property-update-status
                    [edited]="feeDiscountData[discount.key].edited"
                    [hasAirbnbListing]="property.counts?.airbnb > 0"
                    class="@md:hidden mr-[2px]"></sbnb-property-update-status>
                </span>
                <span *ngIf="discount.description" class="text-sm text-night">
                  {{ discount.description }}
                </span>
              </span>
              <sbnb-property-update-status
                [edited]="feeDiscountData[discount.key].edited"
                [hasAirbnbListing]="property.counts?.airbnb > 0"
                class="hidden @md:block"></sbnb-property-update-status>
              <mat-form-field class="input__mini w-full nopadding @md:!max-w-[120px]">
                <input
                  matInput
                  inputmode="numeric"
                  maxlength="6"
                  autocomplete="off"
                  placeholder="0"
                  [(ngModel)]="feeDiscountData[discount.key].value"
                  (change)="savePriceChange(discount.key, feeDiscountData[discount.key])" />
                <span matSuffix class="suffix-bg">%</span>
              </mat-form-field>
            </div>
          </ng-container>
          <ng-container *ngSwitchCase="'early_bird_discount'">
            <div
              *ngIf="featureFlagAirbnbDiscountsEnabled$ | async"
              class="flex @md:gap-3 gap-1 mb-4 @md:mb-2 @md:flex-row flex-col">
              <span class="flex-1 text-sm flex flex-col gap-1">
                <span class="flex items-center justify-between">
                  <span class="strong flex-1">{{ discount.label }}</span>
                  <sbnb-icon-airbnb
                    [size]="12"
                    [matTooltip]="'This data is periodically refreshed from Airbnb.'"
                    class="@md:hidden mr-[2px]"></sbnb-icon-airbnb>
                </span>
                <span *ngIf="discount.description" class="text-sm text-night">
                  {{ discount.description }}
                </span>
              </span>
              <div class="@md:self-end">
                <div
                  class="flex items-center gap-3 mb-2 relative @md:-mr-[1.75rem]"
                  *ngFor="let early of feeDiscountData[discount.key]; let i = index">
                  <sbnb-icon-airbnb
                    [size]="12"
                    [matTooltip]="'This data is periodically refreshed from Airbnb.'"
                    class="hidden @md:block"></sbnb-icon-airbnb>
                  <mat-form-field class="nopadding input__mini !w-40">
                    <input
                      matInput
                      inputmode="numeric"
                      min="1"
                      max="24"
                      autocomplete="off"
                      placeholder="1"
                      [(ngModel)]="early.threshold_days"
                      (change)="checkAndSave(discount.key, feeDiscountData[discount.key])" />
                    <span matSuffix class="whitespace-nowrap">mos before</span>
                  </mat-form-field>
                  <mat-form-field class="input__mini nopadding @md:!max-w-[120px]">
                    <input
                      matInput
                      inputmode="numeric"
                      min="1"
                      max="100"
                      placeholder="0"
                      [(ngModel)]="early.value"
                      (change)="checkAndSave(discount.key, feeDiscountData[discount.key])" />
                    <span matSuffix class="suffix-bg">%</span>
                  </mat-form-field>
                  <sbnb-icon
                    icon="trash"
                    [size]="16"
                    (click)="removeDiscount(discount.key, feeDiscountData[discount.key], i)"
                    class="text-night-lighter hover:text-rose-darker clickable"></sbnb-icon>
                </div>
                <div class="flex justify-end">
                  <sbnb-button
                    size="mini"
                    [type]="'text-secondary'"
                    (click)="addDiscount(feeDiscountData, discount.key)">
                    <span class="text-sm font-medium">+ Add discount</span>
                  </sbnb-button>
                </div>
              </div>
            </div>
          </ng-container>
          <ng-container *ngSwitchCase="'last_minute_discount'">
            <div
              *ngIf="featureFlagAirbnbDiscountsEnabled$ | async"
              class="flex @md:gap-3 gap-1 mb-4 @md:mb-2 @md:flex-row flex-col">
              <span class="flex-1 text-sm flex flex-col gap-1">
                <span class="flex items-center justify-between">
                  <span class="strong flex-1">{{ discount.label }}</span>
                  <sbnb-icon-airbnb
                    [size]="12"
                    [matTooltip]="'This data is periodically refreshed from Airbnb.'"
                    class="@md:hidden mr-[2px]"></sbnb-icon-airbnb>
                </span>
                <span *ngIf="discount.description" class="text-sm text-night">
                  {{ discount.description }}
                </span>
              </span>

              <div class="@md:self-end">
                <div
                  class="flex items-center gap-3 mb-2 relative @md:-mr-[1.75rem]"
                  *ngFor="let lastMinuteDiscount of feeDiscountData[discount.key]; let i = index">
                  <sbnb-icon-airbnb
                    [size]="12"
                    [matTooltip]="'This data is periodically refreshed from Airbnb.'"
                    class="hidden @md:block mr-[2px]"></sbnb-icon-airbnb>
                  <mat-form-field class="nopadding input__mini !w-40">
                    <input
                      matInput
                      inputmode="numeric"
                      min="1"
                      max="28"
                      autocomplete="off"
                      placeholder="1"
                      [(ngModel)]="lastMinuteDiscount.threshold_days"
                      (change)="checkAndSave(discount.key, feeDiscountData[discount.key])" />
                    <span matSuffix class="whitespace-nowrap">days before</span>
                  </mat-form-field>
                  <mat-form-field class="input__mini nopadding @md:!max-w-[120px]">
                    <input
                      matInput
                      inputmode="numeric"
                      min="1"
                      max="100"
                      placeholder="0"
                      [(ngModel)]="lastMinuteDiscount.value"
                      (change)="checkAndSave(discount.key, feeDiscountData[discount.key])" />
                    <span matSuffix class="suffix-bg">%</span>
                  </mat-form-field>
                  <sbnb-icon
                    icon="trash"
                    [size]="16"
                    (click)="removeDiscount(discount.key, feeDiscountData[discount.key], i)"
                    class="text-night-lighter hover:text-rose-darker clickable"></sbnb-icon>
                </div>
                <div class="flex justify-end">
                  <sbnb-button
                    size="mini"
                    [type]="'text-secondary'"
                    (click)="addDiscount(feeDiscountData, discount.key)">
                    <span class="text-sm font-medium">+ Add discount</span>
                  </sbnb-button>
                </div>
              </div>
            </div>
          </ng-container>
        </ng-container>
      </ng-container>
    </div>

    <!-- Taxes -->
    <mat-divider *ngIf="enableTaxes && !section" class="!my-6"></mat-divider>
    <div *ngIf="enableTaxes && !section">
      <div class="flex items-center gap-3 mb-4">
        <h3 class="flex-1" id="taxes">Taxes</h3>
        <sbnb-button type="secondary" size="mini" [routerLink]="['/settings/taxes/name']">+ Add new</sbnb-button>
      </div>

      <ng-container *ngIf="taxes">
        <sbnb-tax-selection [property]="property" [selectedTaxesUuids]="taxesUuids"></sbnb-tax-selection>
      </ng-container>
    </div>

    <!-- Icon legend -->
    <ng-template #iconLegend>
      <a class="link-grape @md:hidden" (click)="showIconLegend = !showIconLegend">
        {{ showIconLegend ? 'Hide' : 'Show' }} what the logos mean
      </a>
      <div *ngIf="showIconLegend" class="">
        <div class="flex items-center gap-3 mb-2">
          <sbnb-property-update-status [hasAirbnbListing]="true" [hasDirectSites]="false"></sbnb-property-update-status>
          <span class="text-sm">{{ status.Airbnb }}</span>
        </div>

        <div class="flex items-center gap-3 mb-2">
          <sbnb-property-update-status
            [hasAirbnbListing]="true"
            [hasDirectSites]="false"
            [edited]="true"></sbnb-property-update-status>
          <span class="text-sm">{{ status.AirbnbEdited }}</span>
        </div>

        <div class="flex items-center gap-3 mb-2">
          <sbnb-property-update-status
            [hasAirbnbListing]="false"
            [hasDirectSites]="false"
            [hasBookingListing]="true"></sbnb-property-update-status>
          <span class="text-sm">{{ status.Booking }}</span>
        </div>

        <div class="flex items-center gap-3">
          <sbnb-property-update-status [hasAirbnbListing]="false"></sbnb-property-update-status>
          <span class="text-sm flex-1">{{ status.DirectSites }}</span>
        </div>
      </div>
    </ng-template>

    <!-- Mobile legend for update icons -->
  </div>
</ng-container>
